import React, { useEffect, useMemo, useState } from 'react';

import {
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from '@mui/material';
import { gridFilterModelSelector } from '@mui/x-data-grid-premium';
import type { GridApi } from '@mui/x-data-grid-premium';
import { matchSorter } from 'match-sorter';
import { equals } from 'ramda';
import { AlineInput } from 'src/AlineInput';
import { Apollo } from 'src/gql/generated';
import { getSelectedGroup, useFindOrCreateLabel, useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import type { ReactSetStateAction, ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import {
  createUpdateAllDocsMsg,
  UpdateDocumentsAuthorizationAlert,
} from './ChangeContractTypeDialog';
import {
  AlineButton,
  AlineCheckbox,
  AlineDialog,
  AlineTooltip,
  CenteredLoader,
  ConfirmationDialog,
} from '../generic';
import { AddIcon } from '../icons/AddIcon';

const useStyles = makeStyles()(theme => ({
  centeredLoader: {
    height: 240,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: 0,
  },
  empty: {
    alignItems: 'center',
    display: 'flex',
    height: 320,
    justifyContent: 'center',
  },
  formGroup: {
    display: 'grid',
    gap: theme.spacing(1),
    gridAutoRows: 'max-content',
    gridTemplateColumns: 'repeat(2, 1fr)',
    height: 320,
    overflow: 'auto',
  },
  label: {
    height: 'fit-content',
    marginLeft: 0,
  },
  paper: {
    width: theme.dialogStyles.standardDialogWidth,
  },
  tooltipWrapper: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
}));

type CheckLabel = {
  id: UUID;
  labelText: string;
  state: Apollo.DocumentLabelState;
};
type CheckLabelState = {
  [key: UUID]: CheckLabel;
};

export function BulkSetDocumentLabels({
  apiRef,
  onClose,
  open,
  reportId,
  runAllDocs,
  selectedDocumentIds,
  totalDocumentCount,
}: {
  apiRef: React.RefObject<GridApi | null>;
  onClose: () => void;
  open: boolean;
  runAllDocs: boolean;
  selectedDocumentIds: UUID[];
  totalDocumentCount: number;
  reportId?: UUID;
}) {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const [search, setSearch] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [bulkSetDocumentLabels, { loading: bulkLoading }] =
    Apollo.useBulkSetDocumentLabelsMutation();
  const { findOrCreateLabel, loading: creatingLabel } = useFindOrCreateLabel();
  const {
    checkBoxLabels,
    loading: loadingLabels,
    refetch,
    stateTuple: [state, setState],
  } = useDocumentCheckBoxLabels({
    apiRef,
    reportId,
    runAllDocs,
    selectedDocumentIds,
  });
  const filteredBySearchLabels = useMemo(() => {
    if (!search) return Object.values(state);

    return matchSorter(Object.values(state), search, {
      keys: ['labelText' satisfies keyof CheckLabel],
    });
  }, [search, state]);
  const searchMatchesDuplicateLabel = Object.values(checkBoxLabels).some(
    label => label.labelText.toLowerCase() === search.toLowerCase()
  );
  const dirty = !equals(state, checkBoxLabels);

  async function handleSubmit() {
    // v9 useGridApiRef returns RefObject<X | null>; gridFilterModelSelector
    // reads from apiRef.current internally so guard against the pre-mount case.
    if (!apiRef.current) return;
    const filterModel = gridFilterModelSelector(apiRef);
    const selectedGroup = getSelectedGroup(apiRef);
    const effectiveRunAll = Boolean(selectedGroup) || runAllDocs;
    const { data } = await bulkSetDocumentLabels({
      variables: {
        input: {
          documentIds: effectiveRunAll ? [] : selectedDocumentIds,
          filterModel,
          groupSelection: selectedGroup?.groupSelection ?? null,
          labelIdsToAdd: Object.values(state)
            .filter(label => label.state === Apollo.DocumentLabelState.Checked)
            .map(label => label.id),
          labelIdsToRemove: Object.values(state)
            .filter(label => label.state === Apollo.DocumentLabelState.NotChecked)
            .map(label => label.id),
          reportId,
          runAllDocs: effectiveRunAll,
        },
      },
    });
    const numSuccessString = `Set labels for ${data?.bulkSetDocumentLabels.numSuccess} documents.`;

    if (data?.bulkSetDocumentLabels) {
      dispatch({
        payload: {
          autoHideDuration: 15000,
          key: uuid4(),
          message: data?.bulkSetDocumentLabels.numSkipped
            ? numSuccessString +
              ` ${data?.bulkSetDocumentLabels.numSkipped} documents were skipped.`
            : numSuccessString,
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      apiRef.current?.setRowSelectionModel({ ids: new Set(), type: 'include' });
    }
    refetch();
    onClose();
  }

  return (
    <AlineDialog
      fullWidth
      TransitionProps={{
        onEnter: () => setState(checkBoxLabels),
        onExit: () => setSearch(''),
      }}
      classes={{ paper: classes.paper }}
      open={open}
      title="Set document labels?"
      onClose={onClose}
    >
      <DialogContent className={classes.dialogContent}>
        <UpdateDocumentsAuthorizationAlert />
        <AlineInput
          autoFocus
          endAdornment={
            <AlineTooltip
              arrow
              placement="top"
              title={
                loadingLabels
                  ? 'Loading labels...'
                  : search
                    ? `Create label "${search}"`
                    : 'Type to create a label...'
              }
            >
              <div className={classes.tooltipWrapper}>
                <IconButton
                  disabled={
                    !search || creatingLabel || searchMatchesDuplicateLabel || loadingLabels
                  }
                  size="small"
                  onClick={async () => {
                    const newLabel = await findOrCreateLabel(search);
                    if (newLabel)
                      setState(prev => ({
                        ...prev,
                        [newLabel.id]: {
                          id: newLabel.id,
                          labelText: newLabel.labelText,
                          state: Apollo.DocumentLabelState.Checked,
                        },
                      }));
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </AlineTooltip>
          }
          placeholder="Search labels..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <CheckLabels
          checkLabels={filteredBySearchLabels}
          loading={loadingLabels}
          search={search}
          setState={setState}
        />
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!dirty || loadingLabels}
          loading={bulkLoading}
          variant="contained"
          onClick={async () => {
            if (runAllDocs) return setConfirmOpen(true);

            await handleSubmit();
          }}
        >
          Save
        </AlineButton>
      </DialogActions>
      <ConfirmationDialog
        message={createUpdateAllDocsMsg('set labels', totalDocumentCount)}
        open={confirmOpen}
        primaryText="Yes"
        secondaryText="No"
        title="Set labels for all documents?"
        onClickPrimary={handleSubmit}
        onClose={() => setConfirmOpen(false)}
      />
    </AlineDialog>
  );
}

function CheckLabels({
  checkLabels,
  loading,
  search,
  setState,
}: {
  checkLabels: CheckLabel[];
  loading: boolean;
  search: string;
  setState: ReactSetStateAction<CheckLabelState>;
}): JSX.Element {
  const { classes } = useStyles();
  const emptyMsg = search
    ? `No labels. Adjust your search or click plus to create the "${search}" label.`
    : 'No labels. Type to search or create a new label.';

  if (loading) return <CenteredLoader className={classes.centeredLoader} />;

  if (!checkLabels.length)
    return (
      <div className={classes.empty}>
        <Typography align="center" variant="subtitle1">
          {emptyMsg}
        </Typography>
      </div>
    );

  return (
    <FormControl>
      <FormGroup className={classes.formGroup}>
        {checkLabels.map(label => {
          return (
            <FormControlLabel
              key={label.id}
              className={classes.label}
              control={
                <AlineCheckbox
                  checked={label.state === Apollo.DocumentLabelState.Checked}
                  indeterminate={label.state === Apollo.DocumentLabelState.Indeterminate}
                  onChange={() => {
                    setState(state => ({
                      ...state,
                      [label.id]: {
                        ...label,
                        state: [
                          Apollo.DocumentLabelState.Checked,
                          Apollo.DocumentLabelState.Indeterminate,
                        ].includes(label.state)
                          ? Apollo.DocumentLabelState.NotChecked
                          : Apollo.DocumentLabelState.Checked,
                      },
                    }));
                  }}
                />
              }
              label={label.labelText}
            />
          );
        })}
      </FormGroup>
    </FormControl>
  );
}

function useDocumentCheckBoxLabels(args: {
  apiRef: React.RefObject<GridApi | null>;
  runAllDocs: boolean;
  selectedDocumentIds: UUID[];
  reportId?: UUID;
}): {
  checkBoxLabels: CheckLabelState;
  loading: boolean;
  refetch: () => void;
  stateTuple: ReactSetStateTuple<CheckLabelState>;
} {
  const { apiRef, reportId, runAllDocs, selectedDocumentIds } = args;
  const [state, setState] = useState<CheckLabelState>({});
  // v9 useGridApiRef returns RefObject<X | null>; gridFilterModelSelector
  // reads from apiRef.current internally so guard against the pre-mount case.
  const filterModel = apiRef.current ? gridFilterModelSelector(apiRef) : undefined;
  const selectedGroup = getSelectedGroup(apiRef);
  const effectiveRunAll = Boolean(selectedGroup) || runAllDocs;
  const { data, loading, refetch } = Apollo.useGetDocumentLabelStateQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        documentIds: effectiveRunAll ? [] : selectedDocumentIds,
        filterModel,
        groupSelection: selectedGroup?.groupSelection ?? null,
        reportId,
        runAllDocs: effectiveRunAll,
      },
    },
  });
  const checkBoxLabels = useMemo(() => {
    return (
      data?.documentLabelStates.reduce<CheckLabelState>((acc, label) => {
        acc[label.id] = {
          id: label.id,
          labelText: label.labelText,
          state: label.state,
        };
        return acc;
      }, {}) ?? {}
    );
  }, [data?.documentLabelStates]);

  useEffect(() => {
    setState(checkBoxLabels);
  }, [checkBoxLabels]);

  return { checkBoxLabels, loading, refetch, stateTuple: [state, setState] };
}
